import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Chip, Card, CardContent, Grid, TablePagination, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, useTheme
} from '@mui/material';
import {
  People as Icon, Add as AddIcon, CheckCircle as ActiveIcon,
  Block as InactiveIcon, Search as SearchIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';

const fetchMensajeros = async () => {
  const res = await fetch('/api/mensajeros', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

const createMensajero = async (data) => {
  const res = await fetch('/api/mensajeros', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify(data)
  });
  return res.json();
};

const toggleEstado = async ({ id, estado }) => {
  const res = await fetch(`/api/mensajeros/${id}/estado`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify({ estado })
  });
  return res.json();
};

export default function Mensajeros() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', password: '', telefono: '', documento: '', vehiculo_tipo: '', vehiculo_placa: '' });

  const { data } = useQuery('mensajeros', fetchMensajeros, { refetchInterval: 15000 });
  const mensajeros = data?.data || [];

  const createMutation = useMutation(createMensajero, {
    onSuccess: () => { queryClient.invalidateQueries('mensajeros'); setOpen(false); setForm({ nombre: '', email: '', password: '', telefono: '', documento: '', vehiculo_tipo: '', vehiculo_placa: '' }); }
  });

  const toggleMutation = useMutation(toggleEstado, { onSuccess: () => queryClient.invalidateQueries('mensajeros') });

  const handleToggle = (m) => {
    toggleMutation.mutate({ id: m.id, estado: m.estado === 'activo' ? 'inactivo' : 'activo' });
  };

  const filtered = mensajeros.filter(m =>
    !search || m.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.telefono?.includes(search) ||
    m.vehiculo_placa?.toLowerCase().includes(search.toLowerCase())
  );

  const pageData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const activos = mensajeros.filter(m => m.estado === 'activo');
  const ocupados = mensajeros.filter(m => m.estado === 'ocupado');
  const califProm = mensajeros.reduce((s, m) => s + parseFloat(m.calificacion || 0), 0) / (mensajeros.length || 1);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Mensajeros</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ ml: 'auto' }}>
          Crear Mensajero
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Total</Typography>
          <Typography variant="h5">{mensajeros.length}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Disponibles</Typography>
          <Typography variant="h5" color="success.main">{activos.length}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">En Servicio</Typography>
          <Typography variant="h5" color="warning.main">{ocupados.length}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Calificación Prom</Typography>
          <Typography variant="h5">{califProm.toFixed(1)} ★</Typography>
        </CardContent></Card></Grid>
      </Grid>

      <TextField
        placeholder="Buscar mensajeros..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
        sx={{ mb: 2, width: 320 }}
        InputProps={{ startAdornment: <SearchIcon sx={{ color: theme.palette.text.secondary, mr: 1, fontSize: 20 }} /> }}
        size="small"
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Teléfono</strong></TableCell>
              <TableCell><strong>Vehículo</strong></TableCell>
              <TableCell><strong>Placa</strong></TableCell>
              <TableCell><strong>Documento</strong></TableCell>
              <TableCell><strong>Calif.</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Acción</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageData.map(m => (
              <TableRow key={m.id} hover>
                <TableCell>{m.nombre || '—'}</TableCell>
                <TableCell>{m.email || '—'}</TableCell>
                <TableCell>{m.telefono || '—'}</TableCell>
                <TableCell>{m.vehiculo_tipo || '—'}</TableCell>
                <TableCell>{m.vehiculo_placa || '—'}</TableCell>
                <TableCell>{m.documento || '—'}</TableCell>
                <TableCell>{m.calificacion || '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={m.estado} size="small"
                    color={m.estado === 'activo' ? 'success' : m.estado === 'ocupado' ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={m.estado === 'activo' ? 'Desactivar' : 'Activar'}>
                    <IconButton size="small" onClick={() => handleToggle(m)} color={m.estado === 'activo' ? 'error' : 'success'}>
                      {m.estado === 'activo' ? <InactiveIcon /> : <ActiveIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {pageData.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center"><Typography color="textSecondary">Sin resultados</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={filtered.length} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Mensajero</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Nombre completo" fullWidth size="small" value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
            <TextField label="Email" fullWidth size="small" type="email" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <TextField label="Contraseña" fullWidth size="small" type="password" value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            <TextField label="Teléfono" fullWidth size="small" value={form.telefono}
              onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} />
            <TextField label="Documento" fullWidth size="small" value={form.documento}
              onChange={e => setForm(f => ({ ...f, documento: e.target.value }))} />
            <TextField label="Tipo vehículo" fullWidth size="small" value={form.vehiculo_tipo}
              onChange={e => setForm(f => ({ ...f, vehiculo_tipo: e.target.value }))} />
            <TextField label="Placa" fullWidth size="small" value={form.vehiculo_placa}
              onChange={e => setForm(f => ({ ...f, vehiculo_placa: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => createMutation.mutate(form)}
            disabled={!form.nombre || !form.email || !form.password}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
